import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardActionArea, CardContent } from "@mui/material";
import { useWorkerProfilePicture } from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Card, useModalState } from "@src/appV2/redesign/system/ui-components";
import {
  ReviewSource,
  type WorkplaceCommentType,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { useDelayedWorkplaceReviewFlag } from "@src/appV2/redesign/Workplace/Review/useDelayedWorkplaceReviewFlag";
import { parseISO } from "date-fns";

import { CardDelayedLayout } from "./CardDelayedLayout";
import { CardStandardLayout } from "./CardStandardLayout";
import { ConfirmAnonymizeCommentBottomSheet } from "./ConfirmAnonymizeCommentBottomSheet";
import { ConfirmRemoveCommentBottomSheet } from "./ConfirmRemoveCommentBottomSheet";
import { LOCKED_BLUR_SX } from "./LockedReviewOverlay";

interface WorkplaceReviewCommentCardProps {
  comment: WorkplaceCommentType;
  workplaceId: string;
  viewerIsAuthor: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  onAnonymized: (comment: WorkplaceCommentType) => void;
  onRemoved: (commentId: string) => void;
  children?: React.ReactNode;
  isLocked?: boolean;
  onUnlockPress?: () => void;
}

export function WorkplaceReviewCommentCard(props: WorkplaceReviewCommentCardProps) {
  const {
    comment,
    workplaceId,
    onClick,
    onLikeChanged,
    viewerIsAuthor,
    onAnonymized,
    onRemoved,
    children,
    isLocked = false,
    onUnlockPress,
  } = props;
  const { createdAt, workerInfo, source, externalDetails } = comment.attributes;
  const { id: workerId } = workerInfo;

  const isExternalReview = isDefined(source) && source !== ReviewSource.CBH;

  const { useDelayedReviewUI } = useDelayedWorkplaceReviewFlag();
  const hasInlineVideo =
    comment.attributes.type === "VIDEO" && isDefined(comment.attributes.media?.[0]?.url);

  useLogEffect(
    APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_LOCKED_COMMENT_VIEWED,
    { workplaceId, commentId: comment.id },
    { enabled: isLocked }
  );

  const anonModalState = useModalState();
  const removeModalState = useModalState();
  const { data: profilePicture, isInitialLoading: isProfilePictureLoading } =
    useWorkerProfilePicture(String(workerId), {
      enabled: isDefined(workerId) && !isExternalReview,
    });

  const profilePictureUrl = isExternalReview
    ? externalDetails?.reviewerPhotoUrl
    : profilePicture?.distribution_url;

  const displayDate = parseISO(
    isExternalReview && isDefined(externalDetails?.publishedAt)
      ? externalDetails.publishedAt
      : createdAt
  );

  const layoutProps = {
    comment,
    workplaceId,
    viewerIsAuthor,
    profilePictureUrl,
    isLoadingProfilePicture: isProfilePictureLoading,
    createdAt: displayDate,
    onLikeChanged,
    onAnonymize: () => {
      anonModalState.openModal();
    },
    onRemove: () => {
      removeModalState.openModal();
    },
    isLocked,
    onUnlockPress,
    reviewUrl: externalDetails?.reviewUrl,
  };

  return (
    <>
      <Card outlined variant="tertiary">
        {hasInlineVideo ? (
          <CardContent
            sx={{
              padding: 7,
              height: "100%",
              minHeight: "11rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {useDelayedReviewUI ? (
              <CardDelayedLayout {...layoutProps} />
            ) : (
              <CardStandardLayout {...layoutProps} />
            )}
          </CardContent>
        ) : (
          <CardActionArea onClick={isLocked ? undefined : onClick}>
            <CardContent
              sx={{
                padding: 7,
                height: "100%",
                minHeight: "11rem",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              {useDelayedReviewUI ? (
                <CardDelayedLayout {...layoutProps} />
              ) : (
                <CardStandardLayout {...layoutProps} />
              )}
            </CardContent>
          </CardActionArea>
        )}
        {isLocked ? <Box sx={LOCKED_BLUR_SX}>{children}</Box> : children}
      </Card>
      {viewerIsAuthor && !isExternalReview && (
        <ConfirmAnonymizeCommentBottomSheet
          modalState={anonModalState}
          commentId={comment.id}
          workplaceId={workplaceId}
          onAnonymized={onAnonymized}
        />
      )}
      {viewerIsAuthor && !isExternalReview && (
        <ConfirmRemoveCommentBottomSheet
          modalState={removeModalState}
          commentId={comment.id}
          workplaceId={workplaceId}
          onRemoved={() => {
            onRemoved(comment.id);
          }}
        />
      )}
    </>
  );
}
